import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ApiButton, Box, IconButton, Typography, useSnackbarHandler } from 'web-library';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import DataTable from 'web-library/stories/organism/DataTable/DataTable';
import DrawerWithDataTable from 'web-library/stories/organism/SwipeableEdgeDrawer/DrawerWithDataTable';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { SubjectDetails, ViewSelectedSubjectsResponseBody } from '../../api/candidate/exam/model';
import { candidateService } from '../../api';
import {
  GET_EXAM_REGISTRATION,
  GET_SELECTED_SUBJECTS,
} from '../../api/candidate/exam/@candidate/queries-services/keys';
import EditExamSubjectsModal, { IEditStateHandler } from '../EditExamSubjectsModal';
import { onEditModalOpenHandler } from '../../utils/ApiUtils';
import { getKeyByValue } from '../../utils/ArrayUtils';
import { setSubjectDetails } from '../../utils/StringUtils';

import {typographyTheme} from "web-library/theme/helper/tssHelper"
// @ts-ignore
const useStyles = makeStyles()((theme) =>
({
  dataRow: {
    display: 'flex',
    padding: '13px 24px',
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.color.border.borderFocused}`,
    },
  },
  main: {
    marginBottom: theme.spacing(0.5),
     ...typographyTheme(theme, 'body2'),
  },
  subtitle1: {
    // @ts-ignore
    color: theme.color.text.textTertiary,
     ...typographyTheme(theme, 'subtitle2'),
    marginBottom: theme.spacing(0.5),
  },
  subtitle2: {
    // @ts-ignore
    color: theme.color.text.textTertiary,
     ...typographyTheme(theme, 'subtitle2'),
  },
  subject: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 auto',
    maxWidth: '220px',
    overflow: 'hidden',
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  confirmButton: {
    width: '100%',
    marginTop: '24px',
  },
}));

const columns = [
  {
    name: 'subjectSelected',
    label: 'Subject(s)',
  },
];

interface IOptions {
  styling: {
    classes: any;
    theme: AugmentedTheme;
  };
  applicationUuid: string;
  openEditModalStateHandler: IEditStateHandler;
}

const Options = (args: IOptions) => {
  const { styling, applicationUuid, openEditModalStateHandler } = args;
  const { snackbarHandler } = useSnackbarHandler();
  const { t } = useTranslation();
  const { classes, theme } = styling;
  const queryClient = useQueryClient();
  const [deletingSubjectUuid, setDeletingSubjectUuid] = useState<string>('');

  const {
    mutate: deleteSelectedSubjectMutate,
    isLoading: isDeleteSelectedSubjectLoading,
  } = candidateService.exam.candidate.useDeleteExamSubjects(
    (res, variable) => {
      queryClient.invalidateQueries(GET_EXAM_REGISTRATION);
      queryClient.invalidateQueries([GET_SELECTED_SUBJECTS, applicationUuid]);
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>SubjectSnackbar.HasBeenRemoved', {
          subjectDetails: setSubjectDetails(variable.callbackVariable),
        }),
      });
    },
    (err, variable) => {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'error',
        targetData: t('exam->>SubjectSnackbar.UnableToRemove', {
          subjectDetails: setSubjectDetails(variable.callbackVariable),
        }),
        targetAnchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
    },
    () => {
      setDeletingSubjectUuid('');
    }
  );

  const onDelete = (selectedSubjectUuid: string, subjectDetails: SubjectDetails) => {
    setDeletingSubjectUuid(selectedSubjectUuid);
    deleteSelectedSubjectMutate({
      applicationUuid: args.applicationUuid,
      examRegistrationDetailUuid: selectedSubjectUuid,
      callbackVariable: subjectDetails,
    });
  };

  return {
    customRowRender: (data: ViewSelectedSubjectsResponseBody[]) =>
      data.length > 0 &&
      data.map((d: ViewSelectedSubjectsResponseBody, idx: number) => (
        <Box key={d.examRegistrationDetailUuid} className={classes.dataRow}>
          <Box className={classes.subject}>
            <Typography className={classes.main} color="textPrimary">
              {`${d.subjectName} (${d.subjectCode})`}
            </Typography>
            {d.optionalPapers.length > 0 && (
              <Typography className={classes.subtitle1} variant="subtitle2">
                + {d.optionalPapers.length} Optional Paper(s)
              </Typography>
            )}
            <Typography className={classes.subtitle2} variant="subtitle2">
              {d.examLevel}
            </Typography>
          </Box>
          <Box className={classes.actionButtons}>
            {d.editable && (
              <IconButton
                name={IconName.edit}
                color={theme.color.icon.iconSelected}
                ariaLabel="edit subject"
                iconSize="S"
                disabled={isDeleteSelectedSubjectLoading}
                onClick={() =>
                  onEditModalOpenHandler(d.examRegistrationDetailUuid, openEditModalStateHandler)
                } />
            )}
            <IconButton
              name={IconName.trash}
              color={theme.color.icon.iconTrash}
              id={`button-delete-subject-${idx}`}
              ariaLabel="delete subject"
              iconSize="S"
              onClick={() => {
                onDelete(d.examRegistrationDetailUuid, {
                  subjectLevel: d.subjectLevel,
                  subjectCode: d.subjectCode,
                  subjectName: d.subjectName,
                });
              }}
              disabled={isDeleteSelectedSubjectLoading}
              isLoading={deletingSubjectUuid === d.examRegistrationDetailUuid}
            />
          </Box>
        </Box>
      )),
  };
};

const transformData = (data: ViewSelectedSubjectsResponseBody[]) =>
  data.map((d) => ({
    subjectSelected: {
      ...d,
    },
  }));

interface ISelectedSubjectsDrawer {
  data: ViewSelectedSubjectsResponseBody[] | [];
  confirmRouteAndLabel: {
    confirmRoute: string;
    confirmLabel: string;
  };
}

const SelectedSubjectsDrawer = (args: ISelectedSubjectsDrawer) => {
  const { data, confirmRouteAndLabel } = args;
  const { confirmRoute, confirmLabel } = confirmRouteAndLabel;
  const { classes } = useStyles();
  const { uuid: applicationUuid } = useParams<any>();
  const theme = useTheme();
  const history = useHistory();
  const [openEditModal, setOpenEditModal] = useState<Record<string, boolean>>({});
  const openEditModalStateHandler = { openEditModal, setOpenEditModal };
  return (
    <>
      <DrawerWithDataTable numOfRows={data.length}>
        <DataTable
          components={[]}
          data={transformData(data)}
          columns={columns}
          options={Options({
            styling: {
              classes,
              theme,
            },
            applicationUuid,
            openEditModalStateHandler,
          })}
          emptyDataLabel="You have no subjects selected"
        />
        <ApiButton
          status="idle"
          size="medium"
          successLabel="Saved "
          errorLabel="Error"
          color="secondary"
          variant="contained"
          disabled={data.length === 0}
          onClick={() => history.push(confirmRoute)}
          className={classes.confirmButton}
        >
          {confirmLabel}
        </ApiButton>
      </DrawerWithDataTable>
      <EditExamSubjectsModal
        applicationUuid={applicationUuid}
        examRegistrationDetailUuid={getKeyByValue(openEditModal, true)}
        editModalHandler={openEditModalStateHandler}
      />
    </>
  );
};

export default SelectedSubjectsDrawer;
